import humps from "humps"
import i18n from "i18next"
import authService from "plugins/Auth"
import { default as layoutSo, default as layoutStore } from "stores/layout"
import dialogSo, { DIALOG_TYPE, DialogMsg } from "stores/layout/dialogStore"
import userSo from "stores/user"



const isPLC = import.meta.env.VITE_TARGET == "plc"

interface OptionsParams {
	/** (true) request for login */
	isLogin?: boolean
	noAuth?: boolean
	/** (true) no show busy indictor */
	noBusy?: boolean
	/** (true) no show dialog errors */
	noDialogError?: boolean
	noCamelize?: boolean
	noDecamelize?: boolean
	/** { <status>: { <dialog settings> } } */
	errorOverride?: any,
	baseUrl?: string,
	blob?: boolean,
}

const optionsParamDefault: OptionsParams = {
	isLogin: false,
	noAuth: false,
	noBusy: false,
	noDialogError: false,
	noCamelize: false,
	noDecamelize: false,
	errorOverride: {},
	baseUrl: null,
	blob: false,
}

export class AjaxService {
	private options: OptionsParams

	constructor() {
		this.options = {
			baseUrl: import.meta.env.VITE_API_MOCK == "true" || !import.meta.env.VITE_API_API_HOST
				? "/api/"
				: import.meta.env.VITE_API_API_HOST
		}
	}

	async post(url: string, data?: any, options?: OptionsParams) {
		return await this.send(url, "POST", data, options)
	}
	async get(url: string, data?: any, options?: OptionsParams) {
		return await this.send(url, "GET", data, options)
	}
	async patch(url: string, data?: any, options?: OptionsParams) {
		return await this.send(url, "PATCH", data, options)
	}
	async put(url: string, data?: any, options?: OptionsParams) {
		return await this.send(url, "PUT", data, options)
	}
	async delete(url: string, data?: any, options?: OptionsParams) {
		return await this.send(url, "DELETE", data, options)
	}

	/**
	 * Send a ajax to server
	 */
	async send(url: string, method: string, data: any, options: OptionsParams = {}) {
		const { setBusy } = layoutStore
		const { logout } = userSo
		options = { ...optionsParamDefault, ...options }

		if (!options.noBusy) setBusy(true)

		// send request
		const isFormData = data instanceof FormData
		if (!options.noDecamelize && !isFormData) data = humps.decamelizeKeys(data)


		//#region  HEADER

		const headers: any = {
			"Accept": "application/json",
		}
		if (!isFormData) headers["Content-Type"] = "application/json"

		const authDisabled = options.isLogin || options.noAuth
		const bearerToken = authService.getBearerToken()
		if (!authDisabled && !!bearerToken) {
			headers["Authorization"] = bearerToken
		}

		//#endregion


		let response: Response
		const baseUrl = options.baseUrl ?? this.options.baseUrl

		try {
			response = await fetch(
				`${baseUrl}${url}`,
				{
					method: method,
					headers,
					body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
					...(options.isLogin && !options.noAuth ? { credentials: 'include' } : { credentials: "omit" })
				}
			)

		} catch (error) {
			dialogSo.dialogOpen({
				type: DIALOG_TYPE.ERROR, title: "FETCH ERROR",
				text: error.message, labelOk: "ok",
			})
			throw error

		} finally {
			// setto l'interfaccia
			if (!options.noBusy) setBusy(false)
		}

		// prelevo i dati
		let ret = null
		let token = null
		try {
			token = response.headers.get("authorization")
			if (!options.blob) {
				ret = await response.json()
				if (!options.noCamelize) ret = humps.camelizeKeys(ret)
			} else {
				ret = await response.blob()
			}
		} catch (e) { }

		// MANAGE ERRORS
		const status = response.status
		if (status >= 400) {

			if (status == 401) {
				await logout({ noHttp: true })
				if (authDisabled) {
					throw ret
					//error = { code: "login" }
				} else {
					dialogSo.dialogOpen({ type: DIALOG_TYPE.ERROR, modal: false, text: i18n.t("text-err.token-expired") })
					throw response
				}
			}

			if (status == 403) history.back()

			// open dialog
			if (!options.noDialogError) {
				const errors = getErrorFromResponse(ret)
				const title = i18n.t(getI18nPathForError(status, url, errors[0].code, "title"), errors[0].data)
				const labelOk = i18n.t(getI18nPathForError(status, url, errors[0].code, "ok"), errors[0].data)
				const text = errors.reduce((msg, error) =>
					msg + "\n" + i18n.t(getI18nPathForError(status, url, error.code, "text"), error.data)
					, "")
				const msgDefault: DialogMsg = {
					type: status == 403 ? DIALOG_TYPE.WARNING : DIALOG_TYPE.ERROR,
					title, text, labelOk, labelCancel: null,
				}
				const msg = { ...msgDefault, ...(options.errorOverride[`${status}`] ?? {}) }
				layoutSo.setLoadingMsg(null)
				await dialogSo.dialogOpen(msg)
			}

			// throw error
			throw ret
		}

		return options.isLogin ? token : ret
	}
}

/**
 * find correct text in i18n json 
 */
function getI18nPathForError(status: string | number, url: string, code: string, prop: string) {
	let i: number
	const context = url.slice(0, (i = url.indexOf("/")) != -1 ? i : undefined)
	return [
		`dialog.ajax.${context}.${code}.${prop}`,
		`dialog.ajax.${context}.${prop}`,
		`dialog.ajax.${status}.${code}.${prop}`,
		`dialog.ajax.${status}.${prop}`,
		`dialog.ajax.${prop}`
	]
}

export default new AjaxService()


function getErrorFromResponse(res: any): AjaxError[] {
	if (!!res?.error) {
		return [{ code: res.error }]
	}
	if (!!res?.errors) {
		if (typeof res.errors == "string") return [{ code: res.errors }]
		if (Array.isArray(res.errors) && res.errors.length > 0) {
			return res.errors.map((obj: any) => {
				if (typeof obj == "string") return { code: obj }
				return {
					code: obj?.code ?? "default",
					data: getDataError(obj.data)
				}
			})
		}
	}
	return [{ code: "default" }]
}

function getDataError(data: any): { [key: string]: string } {
	if (data == null) return {}
	if (typeof data == "string") return { "d0": data }
	if (Array.isArray(data) && data.length > 0) {
		return data.reduce((obj, value, index) => {
			obj[`d${index}`] = value;
			return obj;
		}, {})
	}
	return data
}




type AjaxError = {
	code?: string
	data?: { [key: string]: string }
}